/* el-homepage-reviews.js
 *
 * Slider behavior for the homepage reviews carousel.
 *
 * INFINITE LOOP MECHANISM:
 * On init, the script clones the original cards into BEFORE and
 * AFTER copies. The track contains 3 identical sets of cards.
 * Initial scroll position is set to the first card of the middle
 * (original) copy.
 *
 * When the user scrolls past either end of the middle copy, the
 * script silently teleports scrollLeft by one set's width — the
 * cards under the viewport look identical (because they are
 * clones), so the user sees no jump. Same trick used for arrow
 * clicks: smooth scroll happens normally, then teleport runs
 * after the scroll settles.
 *
 * DOTS:
 * Rendered based on the ORIGINAL card count (not the cloned
 * total). Active dot tracks which original card index is
 * currently centered, via modulo arithmetic.
 *
 * MOBILE PEEK:
 * The CSS gives mobile cards 76% width with scroll-padding-
 * inline of 12% on each side. When a card snaps, its left is at
 * 12% of the viewport, so adjacent cards peek by ~12% on each
 * side. With the infinite loop, both peeks always have content.
 */

(function () {
  'use strict';

  const MOBILE_BP = 980;

  function init(slider) {
    if (slider.dataset.hpInit === 'true') return;
    slider.dataset.hpInit = 'true';

    const track = slider.querySelector('[data-track]');
    const dotsContainer = slider.querySelector('[data-dots]');
    const prevBtn = slider.querySelector('[data-prev]');
    const nextBtn = slider.querySelector('[data-next]');

    if (!track) return;

    const originalCount = track.children.length;
    if (originalCount < 2) {
      // No loop needed with 0 or 1 card; just hide dots & arrows.
      if (dotsContainer) dotsContainer.style.display = 'none';
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      return;
    }

    cloneCardsForLoop(track);

    function getCardsPerView() {
      return window.innerWidth <= MOBILE_BP ? 1 : 3;
    }

    function getCardStep() {
      const card = track.children[0];
      if (!card) return 0;
      const styles = window.getComputedStyle(track);
      const gap = parseInt(styles.gap, 10) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function getSetWidth() {
      return getCardStep() * originalCount;
    }

    function getScrollPadStart() {
      const styles = window.getComputedStyle(track);
      return parseFloat(styles.scrollPaddingInlineStart) || 0;
    }

    function jumpToMiddleCopy() {
      // First original card now sits at index = originalCount
      // (after the BEFORE clones).
      const firstOriginal = track.children[originalCount];
      if (!firstOriginal) return;
      const prev = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = firstOriginal.offsetLeft - getScrollPadStart();
      // Force reflow so the change takes effect before behavior restored
      void track.offsetWidth;
      track.style.scrollBehavior = prev;
    }

    function checkInfiniteWrap() {
      const sw = getSetWidth();
      const step = getCardStep();
      if (!sw || !step) return;

      // If we're past the end of the middle copy, jump back
      if (track.scrollLeft >= sw * 2 - step / 2) {
        const prev = track.style.scrollBehavior;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft -= sw;
        void track.offsetWidth;
        track.style.scrollBehavior = prev;
      }
      // If we're before the start of the middle copy, jump forward
      else if (track.scrollLeft < sw - step / 2) {
        const prev = track.style.scrollBehavior;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft += sw;
        void track.offsetWidth;
        track.style.scrollBehavior = prev;
      }
    }

    function getCurrentOriginalIdx() {
      const step = getCardStep();
      if (!step) return 0;
      const visualIdx = Math.round(track.scrollLeft / step);
      // visualIdx is in cloned space [0, originalCount * 3 - 1].
      // Convert to original card index [0, originalCount - 1].
      const relative = visualIdx - originalCount;
      return ((relative % originalCount) + originalCount) % originalCount;
    }

    function renderDots() {
      if (!dotsContainer) return;
      const perView = getCardsPerView();
      const pageCount = Math.ceil(originalCount / perView);

      dotsContainer.innerHTML = '';
      if (pageCount <= 1) {
        dotsContainer.style.display = 'none';
        return;
      }
      dotsContainer.style.display = '';

      for (let i = 0; i < pageCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'homepage-reviews__dot';
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
        dot.addEventListener('click', () => goToPage(i));
        dotsContainer.appendChild(dot);
      }
      updateActiveDot();
    }

    function goToPage(pageIdx) {
      const step = getCardStep();
      const perView = getCardsPerView();
      if (!step) return;

      const targetOriginalIdx = pageIdx * perView;
      // Find the target card in the CURRENT visible copy
      const currentVisual = Math.round(track.scrollLeft / step);
      const currentCopy = Math.floor(currentVisual / originalCount);
      let targetVisual = currentCopy * originalCount + targetOriginalIdx;

      // Bounds check (should always be valid but just in case)
      if (targetVisual < 0 || targetVisual >= track.children.length) {
        targetVisual = originalCount + targetOriginalIdx;
      }

      const targetCard = track.children[targetVisual];
      if (!targetCard) return;

      track.scrollTo({
        left: targetCard.offsetLeft - getScrollPadStart(),
        behavior: 'smooth',
      });
    }

    function updateActiveDot() {
      if (!dotsContainer) return;
      const perView = getCardsPerView();
      const originalIdx = getCurrentOriginalIdx();
      const currentPage = Math.floor(originalIdx / perView);

      const dots = dotsContainer.querySelectorAll('.homepage-reviews__dot');
      dots.forEach((d, i) => {
        d.classList.toggle('is-active', i === currentPage);
      });
    }

    // Arrow buttons: scroll by one page
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const step = getCardStep();
        const perView = getCardsPerView();
        track.scrollBy({ left: -step * perView, behavior: 'smooth' });
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const step = getCardStep();
        const perView = getCardsPerView();
        track.scrollBy({ left: step * perView, behavior: 'smooth' });
      });
    }

    // Scroll listener:
    // - rAF-throttled active-dot updates
    // - debounced wrap check after scroll settles
    let scrollEndTimer = null;
    let scrollRaf = null;
    track.addEventListener('scroll', () => {
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(updateActiveDot);

      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        checkInfiniteWrap();
        updateActiveDot();
      }, 180);
    }, { passive: true });

    // Resize: re-render dots if cards-per-view changed, re-anchor
    let lastPerView = getCardsPerView();
    let resizeRaf = null;
    window.addEventListener('resize', () => {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(() => {
        const newPerView = getCardsPerView();
        if (newPerView !== lastPerView) {
          lastPerView = newPerView;
          renderDots();
        }
        jumpToMiddleCopy();
      });
    }, { passive: true });

    // Initial setup — wait for layout
    renderDots();
    requestAnimationFrame(() => {
      requestAnimationFrame(jumpToMiddleCopy);
    });
  }

  function cloneCardsForLoop(track) {
    const original = Array.from(track.children);
    const fragBefore = document.createDocumentFragment();
    const fragAfter = document.createDocumentFragment();

    original.forEach((card) => {
      const cb = card.cloneNode(true);
      cb.setAttribute('aria-hidden', 'true');
      cb.setAttribute('data-clone', 'before');
      fragBefore.appendChild(cb);

      const ca = card.cloneNode(true);
      ca.setAttribute('aria-hidden', 'true');
      ca.setAttribute('data-clone', 'after');
      fragAfter.appendChild(ca);
    });

    track.insertBefore(fragBefore, track.firstChild);
    track.appendChild(fragAfter);
  }

  function initAll() {
    document.querySelectorAll('[data-homepage-reviews]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when section reloads
  document.addEventListener('shopify:section:load', (e) => {
    const slider = e.target.querySelector('[data-homepage-reviews]');
    if (slider) {
      slider.dataset.hpInit = 'false';
      init(slider);
    }
  });
})();